import React, { useMemo, useRef, useState, useEffect } from "react";
import { FiInfo, FiMinus, FiPlus } from "react-icons/fi";
import {
  GuideCloseButton,
  titleStyle,
  introStyle,
  calloutStyle,
  sectionStyle,
  h2Style,
  h3Style,
  listStyle,
  linkStyle,
  PromptBlock,
} from "./dashboardSkillGuideCommon";

const DASHBOARD_PROMPT_TEMPLATE = `Use the dashboard-skill to build a Munshot embedded dashboard.

Before writing any code, read the skill's reference files and follow them exactly - especially auth-standards.md for the Munshot Dashboard SDK integration (host handshake, useHostContext, token/ticker, and the dashboard.capture.visual / dashboard.capture.snapshot handlers). Do not improvise the SDK setup or the connection lifecycle; copy the lib/sdk.ts and hooks/useHostContext.ts patterns verbatim and obey the "DO NOT" rules (never set autoReady: false, never call sdk.ready() manually). Also follow ui-standards.md (color palette), embed_protection.md (Cloudflare Pages embed protection), chat-agent.md (the "Ask AI" chat panel), and use only datasources from datasource-registry.md.

Dashboard idea:
<describe what the dashboard should do, what the user enters/sees, and what insight it gives>

Inputs / filters:
<e.g. a query box, a URL field, date range, sector dropdown - or "none, use selected ticker">

Datasources to use:
<e.g. muns_chat, web_reader, news_search - or "you choose the best fit from the registry">

Requirements:

Treat everything under .claude/skills/ as read-only reference. Do not edit, rewrite, "improve," reorganize, or regenerate any skill file — read them and follow them only.
Build the actual dashboard as the first screen (no landing page).
Wire it to the Munshot host via the SDK so it receives the session token and selected ticker automatically; authenticate every API call with Authorization: Bearer \${session.token}.
Implement loading, empty, no-ticker, waiting-for-session, partial, and error states.
Ship the "Ask AI" chat panel exactly as specified in chat-agent.md: copy prompts.ts, runAgent.ts and AskAiPanel.tsx verbatim, write dashboardContext.ts and tools.ts for this dashboard, and never put a write-capable datasource in the tool catalog.
Make it production-ready: npm run build must pass with no type errors, and include the Cloudflare static-assets wrangler.jsonc plus both embed-protection files from embed_protection.md.
When done, commit, push to my branch, and give me the deploy steps.`;

const DATASOURCES = [
  {
    name: "web_search",
    desc: "Search the public internet using Brave Search.",
  },
  {
    name: "web_reader",
    desc: "Read and extract content from one or more URLs.",
  },
  { name: "news_search", desc: "Search recent news articles." },
  {
    name: "document_search",
    desc: "Search proprietary documents indexed in Pinecone.",
  },
  {
    name: "muns_chat",
    desc: "Stream an AI answer for a dashboard question using Muns chat context, documents, tickers, and dashboard inputs.",
  },
  {
    name: "agent_run",
    desc: "Run a registered analyst agent and stream its output for dashboard generation or refresh workflows.",
  },
  {
    name: "portfolio_list",
    desc: "Retrieve the user's portfolio or watchlist items with stock details such as ticker, company, sector, and industry.",
  },
  {
    name: "email_send",
    desc: "Send a transactional email (subject + HTML or plain-text body) to a single recipient. Used to deliver designed dashboard content — reports, editions, briefs — to a user's inbox.",
  },
  {
    name: "insider_trades",
    desc: "Fetch insider trades for a ticker as a markdown table. Routes to Indian exchange data (NSE/BSE/Trendlyne) or US Finviz data based on country.",
  },
  {
    name: "drhp_filings",
    desc: "Fetch DRHP/prospectus filings for a company by ticker symbol or exact company name. Returns up to 50 filings with nested document links.",
  },
  {
    name: "combined_financials",
    desc: "Fetch combined financial data as markdown. India path scrapes screener.in; USA path fetches from yfinance.",
  },
  {
    name: "combined_filings_announcements",
    desc: "Fetch filings and announcements from multiple sources (BSE/NSE/DRHP/screener.in for India; SEC via FastAPI for USA). Each item includes an isRead flag.",
  },
  {
    name: "filings_domestic",
    desc: "Fetch concall transcripts, annual reports, and earnings report PDFs for an Indian ticker by scraping screener.in.",
  },
  {
    name: "corp_announcements",
    desc: "Fetch corporate announcements from BSE (primary), NSE (fallback), and DRHP documents. Results grouped by source.",
  },
  {
    name: "financial_tables_markdown",
    desc: "Fetch financial summary as markdown by scraping screener.in. Includes pros/cons, ratios, shareholding, balance sheet, P&L, quarterly results, peer comparison.",
  },
  {
    name: "stock_data",
    desc: "Fetch real-time stock quotes, detailed company info, cash flow, or balance sheet for a ticker. No auth required. Poll at max every 3 seconds to stay safe under the 60 RPM limit.",
  },
  {
    name: "market_data",
    desc: "Fetch historical or intraday OHLCV price series for a ticker over a date range. No auth required. Always pass csv=false to get JSON.",
  },
  {
    name: "stock_data_batch",
    desc: "Fetch real-time stock quotes for up to 80 tickers in a single call. No auth required. Returns per-ticker status so partial failures don't break the whole response.",
  },
  {
    name: "fetch_formula",
    desc: "Fetch formula definitions (name, expression, description) for one or more metric/ratio names using semantic search. Returns definitions only — not data values.",
  },
  {
    name: "financials",
    desc: "Fetch income statement, balance sheet, cash flow statement, and calendar info for a ticker as a markdown string. Sourced from yfinance. No auth required.",
  },
  {
    name: "ratio_source",
    desc: "Look up source URLs for financial ratios/metrics for one or more tickers using semantic search. Returns where to find the data, not the values themselves — follow up with web_reader on the returned URLs.",
  },
  {
    name: "street_estimates",
    desc: "Fetch analyst/broker street estimates for a ticker as a markdown table. India sourced from Trendlyne; USA sourced from TipRanks.",
  },
  {
    name: "drhp_companies",
    desc: "List all unlisted DRHP/IPO companies with optional search, source filter, and pagination. Filters out companies already listed in the stock table — effectively an upcoming/unlisted IPO list.",
  },
  {
    name: "stock_search",
    desc: "Search stocks by company name or ticker across listed stocks, user/org custom stocks, and unlisted DRHP/IPO companies. Supports filters, sorting, and pagination.",
  },
  {
    name: "llm_chat",
    desc: "Send a query to a local or hosted LLM and get the completion back, streamed as NDJSON or as a single JSON response.",
  },
  {
    name: "super_investors_list",
    desc: "List well-known (\"super\") investors and their basic profile (name, slug, bio, image). Scraped live from finology.in.",
  },
  {
    name: "super_investor_portfolio",
    desc: "Get a super investor's portfolio with quarterly holdings by company, keyed by investor slug. Scraped live from finology.in.",
  },
  {
    name: "free_float_market_cap",
    desc: "Fetch the free float market cap (and total market cap) for an Indian listed stock, in ₹ crore. Sourced live from BSE.",
  },
];

const INPUT_FILTER_TYPES = [
  {
    value: "text_input",
    label: "Text input",
    desc: "Use when the user should type a short query, keyword, name, or sentence.",
  },
  {
    value: "textarea_input",
    label: "Long text box",
    desc: "Use when the user should paste or write a longer instruction, brief, or notes.",
  },
  {
    value: "single_select",
    label: "Dropdown / single select",
    desc: "Use when the user should pick one option from a known list.",
  },
  {
    value: "multi_select",
    label: "Multi-select",
    desc: "Use when the user should pick more than one option from a known list.",
  },
  {
    value: "date_input",
    label: "Date or date range",
    desc: "Use when the dashboard needs one date or a start/end date range.",
  },
  {
    value: "number_input",
    label: "Number input",
    desc: "Use when the user should enter a count, limit, percentage, threshold, or score.",
  },
  {
    value: "toggle_input",
    label: "Toggle / checkbox",
    desc: "Use when the user should turn one simple setting on or off.",
  },
  {
    value: "url_input",
    label: "URL input",
    desc: "Use when the user should paste a link for the dashboard to read or analyze.",
  },
];

const promptBuilderCardStyle = {
  backgroundColor: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  padding: "18px",
  marginBottom: "18px",
};

const fieldGroupStyle = {
  marginBottom: "18px",
};

const promptBuilderColumnsStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  gap: "22px",
  alignItems: "start",
  marginBottom: "18px",
};

const builderSectionStyle = {
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  padding: "14px",
};

const generatedPromptNoteStyle = {
  backgroundColor: "#eef2ff",
  border: "1px solid #c7d2fe",
  borderRadius: "8px",
  padding: "12px 14px",
  color: "#312e81",
  fontSize: "13px",
  lineHeight: 1.55,
  marginBottom: "12px",
};

const labelStyle = {
  display: "block",
  color: "#1e293b",
  fontSize: "14px",
  fontWeight: "700",
  marginBottom: "6px",
};

const helperTextStyle = {
  color: "#64748b",
  fontSize: "13px",
  lineHeight: 1.5,
  marginTop: 0,
  marginBottom: "8px",
};

const textareaStyle = {
  width: "100%",
  minHeight: "110px",
  boxSizing: "border-box",
  border: "1px solid #cbd5e1",
  borderRadius: "6px",
  padding: "10px 12px",
  color: "#1e293b",
  fontSize: "14px",
  lineHeight: 1.5,
  resize: "vertical",
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #cbd5e1",
  borderRadius: "6px",
  padding: "10px 12px",
  color: "#1e293b",
  fontSize: "14px",
};

const selectStyle = {
  ...inputStyle,
  backgroundColor: "#ffffff",
};

const selectWithTooltipStyle = {
  display: "grid",
  gridTemplateColumns: "1fr auto",
  gap: "8px",
  alignItems: "center",
};

const builderLineStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(140px, 0.75fr) minmax(220px, 1.25fr) auto auto",
  gap: "8px",
  alignItems: "start",
  marginBottom: "10px",
};

const compactTextareaStyle = {
  ...textareaStyle,
  minHeight: "58px",
  height: "58px",
  paddingTop: "10px",
  paddingBottom: "10px",
};

const buttonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "40px",
  padding: "0 14px",
  backgroundColor: "#3730a3",
  color: "#ffffff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontWeight: "700",
  fontSize: "14px",
};

const errorStyle = {
  color: "#b91c1c",
  fontSize: "13px",
  fontWeight: "600",
  marginTop: "8px",
};

const tooltipWrapperStyle = {
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
};

const tooltipButtonStyle = {
  width: "34px",
  height: "34px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#eef2ff",
  color: "#3730a3",
  border: "1px solid #c7d2fe",
  borderRadius: "999px",
  cursor: "help",
};

const tooltipBubbleStyle = {
  position: "absolute",
  top: "calc(100% + 10px)",
  zIndex: 30,
  width: "260px",
  maxWidth: "min(260px, 70vw)",
  padding: "10px 12px",
  backgroundColor: "#111827",
  color: "#f8fafc",
  borderRadius: "8px",
  boxShadow: "0 14px 30px rgba(15, 23, 42, 0.24)",
  fontSize: "12px",
  lineHeight: 1.45,
};

const tooltipArrowStyle = {
  position: "absolute",
  top: "-5px",
  width: "10px",
  height: "10px",
  backgroundColor: "#111827",
  transform: "rotate(45deg)",
};

const iconButtonStyle = {
  width: "34px",
  height: "34px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid #c7d2fe",
  borderRadius: "6px",
  backgroundColor: "#ffffff",
  color: "#3730a3",
  cursor: "pointer",
};

const removeIconButtonStyle = {
  ...iconButtonStyle,
  borderColor: "#fecaca",
  color: "#b91c1c",
};

const customDropdownWrapperStyle = {
  position: "relative",
  width: "100%",
};

const customDropdownTriggerStyle = {
  ...inputStyle,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  cursor: "pointer",
  backgroundColor: "#ffffff",
  userSelect: "none",
  gap: "6px",
};

const customDropdownListStyle = {
  position: "absolute",
  top: "calc(100% + 2px)",
  left: 0,
  right: 0,
  zIndex: 100,
  backgroundColor: "#ffffff",
  border: "1px solid #cbd5e1",
  borderRadius: "6px",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
  maxHeight: "400px",
  overflowY: "auto",
};

const customDropdownOptionStyle = {
  padding: "10px 14px",
  fontSize: "14px",
  color: "#1e293b",
  cursor: "pointer",
  borderBottom: "1px solid #f1f5f9",
};

const CustomDatasourceSelect = ({ value, onChange, options }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const selectedLabel =
    options.find((o) => o.name === value)?.name || "Select datasource";

  return (
    <div style={customDropdownWrapperStyle} ref={wrapperRef}>
      <div
        style={customDropdownTriggerStyle}
        onClick={() => setOpen((prev) => !prev)}
        role="combobox"
        aria-expanded={open}
        aria-label="Datasource"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setOpen((prev) => !prev);
          if (e.key === "Escape") setOpen(false);
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selectedLabel}
        </span>
        <span style={{ fontSize: "10px", flexShrink: 0 }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div style={customDropdownListStyle} role="listbox">
          {options.map((option) => (
            <div
              key={option.name}
              style={{
                ...customDropdownOptionStyle,
                backgroundColor: option.name === value ? "#eef2ff" : "transparent",
                fontWeight: option.name === value ? "600" : "400",
              }}
              role="option"
              aria-selected={option.name === value}
              onMouseDown={() => {
                onChange(option.name);
                setOpen(false);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f1f5f9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  option.name === value ? "#eef2ff" : "transparent";
              }}
            >
              <div style={{ fontWeight: option.name === value ? "600" : "400" }}>
                {option.name}
              </div>
              {option.desc && (
                <div style={{ fontSize: "13px", color: "#64748b", marginTop: "3px", fontWeight: "400", lineHeight: 1.5 }}>
                  {option.desc}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const emptyInputFilter = () => ({
  id: `${Date.now()}-${Math.random()}`,
  type: "",
  description: "",
});

const emptyDatasource = () => ({
  id: `${Date.now()}-${Math.random()}`,
  name: "",
  description: "",
});

const getInputTypeLabel = (type) =>
  INPUT_FILTER_TYPES.find((option) => option.value === type)?.label || type;

const getInputTypeDescription = (type) =>
  INPUT_FILTER_TYPES.find((option) => option.value === type)?.desc || "";

const getDatasourceDescription = (name, datasourceOptions) =>
  datasourceOptions.find((source) => source.name === name)?.desc || "";

const TooltipHint = ({ text, align = "right" }) => {
  const [isVisible, setIsVisible] = useState(false);

  if (!text) {
    return null;
  }

  return (
    <span
      style={tooltipWrapperStyle}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onFocus={() => setIsVisible(true)}
      onBlur={() => setIsVisible(false)}
    >
      <button
        type="button"
        style={tooltipButtonStyle}
        aria-label="Show description"
      >
        <FiInfo aria-hidden="true" />
      </button>
      {isVisible ? (
        <span
          role="tooltip"
          style={{
            ...tooltipBubbleStyle,
            ...(align === "left" ? { left: 0 } : { right: 0 }),
          }}
        >
          {text}
          <span
            style={{
              ...tooltipArrowStyle,
              ...(align === "left" ? { left: "12px" } : { right: "12px" }),
            }}
          />
        </span>
      ) : null}
    </span>
  );
};

const buildPrompt = ({
  dashboardIdea,
  inputFilters,
  customInputFilter,
  datasources,
  customDatasource,
}) => {
  const inputLines = inputFilters.map((input, index) => {
    return `${index + 1}. ${getInputTypeLabel(input.type)}: ${input.description.trim()}`;
  });

  if (customInputFilter.trim()) {
    inputLines.push(
      `${inputLines.length + 1}. Other / custom input or filter request: ${customInputFilter.trim()}`,
    );
  }

  const inputsText = inputLines.length > 0 ? inputLines.join("\n") : "none";

  const datasourceLines = datasources.map((source) => {
    if (source.name === "you_select_best_fit") {
      return source.description
        ? `- you select the best fit from the registry: ${source.description}`
        : "- you select the best fit from the registry";
    }

    return `- ${source.name}: ${source.description || source.desc}`;
  });

  if (customDatasource.trim()) {
    datasourceLines.push(
      `- Other / custom request: ${customDatasource.trim()}`,
    );
  }

  const datasourcesText =
    datasourceLines.length > 0
      ? datasourceLines.join("\n")
      : "none";

  return DASHBOARD_PROMPT_TEMPLATE.replace(
    "<describe what the dashboard should do, what the user enters/sees, and what insight it gives>",
    dashboardIdea.trim(),
  )
    .replace(
      '<e.g. a query box, a URL field, date range, sector dropdown - or "none, use selected ticker">',
      inputsText,
    )
    .replace(
      '<e.g. muns_chat, web_reader, news_search - or "you choose the best fit from the registry">',
      datasourcesText,
    );
};

const DashboardSkillSetupGuide = ({ onClose }) => {
  const [dashboardIdea, setDashboardIdea] = useState("");
  const [inputFilters, setInputFilters] = useState([emptyInputFilter()]);
  const [customInputFilter, setCustomInputFilter] = useState("");
  const [datasources, setDatasources] = useState([emptyDatasource()]);
  const [customDatasource, setCustomDatasource] = useState("");
  const [generatedPrompt, setGeneratedPrompt] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const datasourceOptions = useMemo(
    () => [
      {
        name: "you_select_best_fit",
        desc: "Let Claude choose the best datasource from the registry for this dashboard.",
      },
      ...DATASOURCES,
    ],
    [],
  );

  const addInputFilter = () => {
    setInputFilters((current) => [...current, emptyInputFilter()]);
  };

  const updateInputFilter = (id, field, value) => {
    setInputFilters((current) =>
      current.map((input) =>
        input.id === id ? { ...input, [field]: value } : input,
      ),
    );
  };

  const removeInputFilter = (id) => {
    setInputFilters((current) => {
      const next = current.filter((input) => input.id !== id);
      return next.length > 0 ? next : [emptyInputFilter()];
    });
  };

  const addDatasource = () => {
    setDatasources((current) => [...current, emptyDatasource()]);
  };

  const updateDatasource = (id, field, value) => {
    setDatasources((current) =>
      current.map((source) =>
        source.id === id ? { ...source, [field]: value } : source,
      ),
    );
  };

  const removeDatasource = (id) => {
    setDatasources((current) => {
      const next = current.filter((source) => source.id !== id);
      return next.length > 0 ? next : [emptyDatasource()];
    });
  };

  const handleGeneratePrompt = () => {
    const trimmedIdea = dashboardIdea.trim();

    if (!trimmedIdea) {
      setErrorMessage("Write the dashboard idea before generating the prompt.");
      setGeneratedPrompt("");
      return;
    }

    const validInputFilters = inputFilters.filter((input) => {
      return input.type && input.description.trim();
    });
    const validDatasources = datasources
      .filter((source) => source.name)
      .map((source) => ({
        ...source,
        desc: getDatasourceDescription(source.name, datasourceOptions),
        description: source.description.trim(),
      }));

    setErrorMessage("");
    setGeneratedPrompt(
      buildPrompt({
        dashboardIdea: trimmedIdea,
        inputFilters: validInputFilters,
        customInputFilter,
        datasources: validDatasources,
        customDatasource,
      }),
    );
  };

  return (
    <div>
      <GuideCloseButton onClose={onClose} />

      <h2 style={titleStyle}>Dashboard Skill Setup For New Repositories</h2>
      <p style={introStyle}>
        This guide is for the dashboard team. It explains how to create a new
        dashboard repo, enable the <code>dashboard-skill</code> in Claude Code,
        and then give Claude the right dashboard-building prompt.
      </p>

      <div style={calloutStyle}>
        <strong>Important:</strong>
        <ul style={{ ...listStyle, marginBottom: 0, marginTop: "8px" }}>
          <li>
            Give Claude only the commands/prompts shown under{" "}
            <strong>Give Claude This</strong>.
          </li>
        </ul>
      </div>

      <section style={sectionStyle}>
        <h3 style={h2Style}>Help / Contact</h3>
        <p style={introStyle}>
          If there is any confusion during setup or dashboard creation,
          contact/tag Tarandeep in the Munshot WhatsApp group or DM him
          personally.
        </p>
      </section>

      <section style={sectionStyle}>
        <h3 style={h2Style}>
          Step 1: Create A Repo From The Dashboard Template
        </h3>
        <ol style={listStyle}>
          <li>
            If you are building in Techmuns GitHub, click this to open the
            dashboard template repo:{" "}
            <a
              href="https://github.com/techmuns/dashboard-template"
              target="_blank"
              rel="noopener noreferrer"
              style={linkStyle}
            >
              https://github.com/techmuns/dashboard-template
            </a>
          </li>
          <li>
            If you are building in Ceekay-Munshot GitHub, click this to open the
            dashboard template repo:{" "}
            <a
              href="https://github.com/ceekay-munshot/dashboard-template"
              target="_blank"
              rel="noopener noreferrer"
              style={linkStyle}
            >
              https://github.com/ceekay-munshot/dashboard-template
            </a>
          </li>
          <li>
            On the right side, click the green button:{" "}
            <code>Use this template</code>
          </li>
          <li>
            Click: <code>Create a new repository</code>
          </li>
          <li>Give your new repo a name.</li>
          <li>
            Click the green button at the bottom: <code>Create repository</code>
          </li>
        </ol>
      </section>

      <section style={sectionStyle}>
        <h3 style={h2Style}>Step 2: Open The New Repo In Claude Code</h3>
        <ol style={listStyle}>
          <li>Go to Claude Code.</li>
          <li>Create a new session.</li>
          <li>
            Select the repository you just created from the dashboard template.
          </li>
          <li>Give the following prompt to Claude Code.</li>
        </ol>
      </section>

      <section style={sectionStyle}>
        <h3 style={h2Style}>Step 3: Generate The Dashboard Prompt</h3>
        <p style={introStyle}>
          Fill the fields below, then generate the final prompt and give that to
          Claude Code.
        </p>

        <div style={promptBuilderCardStyle}>
          <div style={fieldGroupStyle}>
            <label style={labelStyle} htmlFor="dashboard-idea">
              Dashboard idea (required)
            </label>
            <p style={helperTextStyle}>
              Write the idea based on customer requirements, what the dashboard
              should do, what the user should see or enter, what insight it
              should give, and any UI ideas you want Claude to consider.
            </p>
            <textarea
              id="dashboard-idea"
              value={dashboardIdea}
              onChange={(event) => setDashboardIdea(event.target.value)}
              style={textareaStyle}
              placeholder="Describe the dashboard freely here. Include the workflow, output, insight, layout ideas, visual style ideas, tables, charts, cards, or anything else you want Claude to build."
            />
          </div>

          <div style={promptBuilderColumnsStyle}>
            <div style={builderSectionStyle}>
              <label style={labelStyle}>Inputs / filters (optional)</label>
              <p style={helperTextStyle}>
                Add only the inputs or filters you want users to control inside
                the dashboard.
              </p>

              {inputFilters.map((input) => (
                <div key={input.id} style={builderLineStyle}>
                  <div>
                    <div style={selectWithTooltipStyle}>
                      <select
                        value={input.type}
                        onChange={(event) =>
                          updateInputFilter(
                            input.id,
                            "type",
                            event.target.value,
                          )
                        }
                        style={selectStyle}
                        aria-label="Input or filter type"
                        title={getInputTypeDescription(input.type)}
                      >
                        <option value="">Select input type</option>
                        {INPUT_FILTER_TYPES.map((option) => (
                          <option
                            key={option.value}
                            value={option.value}
                            title={option.desc}
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <TooltipHint
                        text={getInputTypeDescription(input.type)}
                        align="left"
                      />
                    </div>
                  </div>
                  <textarea
                    value={input.description}
                    onChange={(event) =>
                      updateInputFilter(
                        input.id,
                        "description",
                        event.target.value,
                      )
                    }
                    style={compactTextareaStyle}
                    placeholder={
                      "Describe what this input/filter should accept."
                    }
                    aria-label="Input or filter description"
                  />
                  <button
                    type="button"
                    style={iconButtonStyle}
                    onClick={addInputFilter}
                    aria-label="Add input or filter row"
                    title="Add another input/filter"
                  >
                    <FiPlus aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    style={removeIconButtonStyle}
                    onClick={() => removeInputFilter(input.id)}
                    aria-label="Remove input or filter row"
                    title="Remove this input/filter"
                  >
                    <FiMinus aria-hidden="true" />
                  </button>
                </div>
              ))}

              <textarea
                value={customInputFilter}
                onChange={(event) => setCustomInputFilter(event.target.value)}
                style={{
                  ...textareaStyle,
                  minHeight: "82px",
                  marginTop: "10px",
                }}
                placeholder="Write any other input/filter need or structure if the listed options do not cover it."
              />
            </div>

            <div style={builderSectionStyle}>
              <label style={labelStyle}>Datasources (optional)</label>
              <p style={helperTextStyle}>
                Choose from the registered datasource list, let Claude pick the
                best fit, or add a custom note.
              </p>

              {datasources.map((source) => {
                const datasourceDescription = getDatasourceDescription(
                  source.name,
                  datasourceOptions,
                );

                return (
                  <div key={source.id} style={builderLineStyle}>
                    <div style={selectWithTooltipStyle}>
                      <CustomDatasourceSelect
                        value={source.name}
                        onChange={(value) =>
                          updateDatasource(source.id, "name", value)
                        }
                        options={datasourceOptions}
                      />
                      <TooltipHint text={datasourceDescription} />
                    </div>
                    <textarea
                      value={source.description}
                      onChange={(event) =>
                        updateDatasource(
                          source.id,
                          "description",
                          event.target.value,
                        )
                      }
                      style={compactTextareaStyle}
                      placeholder="Describe how this datasource should be used."
                      aria-label="Datasource description"
                    />
                    <button
                      type="button"
                      style={iconButtonStyle}
                      onClick={addDatasource}
                      aria-label="Add datasource row"
                      title="Add another datasource"
                    >
                      <FiPlus aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      style={removeIconButtonStyle}
                      onClick={() => removeDatasource(source.id)}
                      aria-label="Remove datasource row"
                      title="Remove this datasource"
                    >
                      <FiMinus aria-hidden="true" />
                    </button>
                  </div>
                );
              })}

              <textarea
                value={customDatasource}
                onChange={(event) => setCustomDatasource(event.target.value)}
                style={{
                  ...textareaStyle,
                  minHeight: "82px",
                  marginTop: "10px",
                }}
                placeholder="Write any other datasource need or instruction if the listed options do not cover it."
              />
            </div>
          </div>

          <button
            type="button"
            style={buttonStyle}
            onClick={handleGeneratePrompt}
          >
            Generate Prompt
          </button>
          {errorMessage ? <div style={errorStyle}>{errorMessage}</div> : null}
        </div>

        {generatedPrompt ? (
          <>
            <h4 style={h3Style}>Generated Dashboard Prompt</h4>
            <p style={generatedPromptNoteStyle}>
              <strong>Note:</strong> Use this generated prompt only once, at
              the start of the Claude Code session. For all later changes,
              improvements, or fixes, continue in your own natural language and
              build the dashboard iteratively.
            </p>
            <PromptBlock give={generatedPrompt} />
          </>
        ) : null}
      </section>
    </div>
  );
};

export default DashboardSkillSetupGuide;
